<template>
  <div id="prodSettBG" class="pricing-costs-page" :class="{ 'pricing-costs-page--settings': inSettingsHub }">
    <div class="page-header">
      <h1 class="page-title">{{ inSettingsHub ? 'Bulk COGS & Margins' : 'Pricing & Costs' }}</h1>
      <button v-if="!inSettingsHub" class="back-button" @click="$router.push('/home')">
        <i class="left chevron icon"></i> Back
      </button>
    </div>

    <div class="page-content">
      <DefaultMarginTargetSection
        :settings="settings"
        :formula-description="formulaDescription"
        :formula-example="formulaExample"
        @margin-change="onMarginChange"
        @toggle-change="onToggleChange" />

      <BulkImportSection
        :statistics="statistics"
        :downloading="downloading"
        :uploading="uploading"
        @download-template="downloadCSVTemplate"
        @file-selected="handleFileSelected"
        @file-removed="handleFileRemoved"
        @error="handleError" />

      <div v-if="successMessage" class="ui success message">
        <i class="close icon" @click="successMessage = ''"></i>
        <div class="header">Success</div>
        <p>{{ successMessage }}</p>
      </div>
      <div v-if="errorMessage" class="ui error message">
        <i class="close icon" @click="errorMessage = ''"></i>
        <div class="header">Error</div>
        <p>{{ errorMessage }}</p>
      </div>
    </div>

    <div class="page-footer">
      <button class="cancel-button" @click="onCancel">Cancel</button>
      <button 
        class="save-button" 
        @click="saveSettings"
        :class="{ loading: saving }">
        Save Changes
      </button>
    </div>
  </div>
</template>

<script>
import DefaultMarginTargetSection from './components/pricing-costs/DefaultMarginTargetSection.vue'
import BulkImportSection from './components/pricing-costs/BulkImportSection.vue'

export default {
  name: 'PricingCostsPage',
  components: {
    DefaultMarginTargetSection,
    BulkImportSection
  },
  data: function () {
    return {
      loading: false,
      saving: false,
      downloading: false,
      uploading: false,
      uploadedFile: null,
      successMessage: '',
      errorMessage: '',
      settings: {
        defaultMarginPercentage: 20.0,
        floorPriceAlertEnabled: true
      },
      statistics: {
        totalProducts: 0,
        costsSet: 0,
        missingCosts: 0,
        percentageSet: 0
      },
      formulaDescription: 'Floor Price = (Landed Cost + Amazon Fees) ÷ (1 - Margin%)',
      formulaExample: {
        landed_cost: 10.50,
        amazon_fees: 7.52,
        margin_percentage: 20.0,
        result: 22.53
      },
      appliedFullPageBodyBg: false
    }
  },
  computed: {
    inSettingsHub: function () {
      return this.$route.name === 'SettingsBulkCogs'
    }
  },
  mounted: function () {
    this.appliedFullPageBodyBg = !this.inSettingsHub
    if (this.appliedFullPageBodyBg) {
      document.body.style.backgroundColor = '#ffffff'
      document.body.style.background = '#ffffff'
    }
    this.loadSettings()
  },
  beforeDestroy: function () {
    if (this.appliedFullPageBodyBg) {
      document.body.style.backgroundColor = ''
      document.body.style.background = ''
    }
  },
  methods: {
    onCancel: function () {
      if (this.inSettingsHub) {
        this.$router.push('/settings/product-tracking')
      } else {
        this.$router.push('/home')
      }
    },
    loadSettings: function () {
      this.loading = true
      this.errorMessage = ''
      
      this.$http.get('/api/v1/pricing_costs/settings').then((response) => {
        this.loading = false
        if (response.body) {
          const data = response.body
          
          // Load default margin target settings
          if (data.default_margin_target) {
            this.settings.defaultMarginPercentage = data.default_margin_target.minimum_margin_percentage || 20.0
            this.settings.floorPriceAlertEnabled = data.default_margin_target.floor_price_alert_enabled !== false
            
            // Load formula example if available
            if (data.default_margin_target.formula && data.default_margin_target.formula.example) {
              const example = data.default_margin_target.formula.example
              this.formulaDescription = data.default_margin_target.formula.description || this.formulaDescription
              this.formulaExample = {
                landed_cost: example.landed_cost || 10.50,
                amazon_fees: example.amazon_fees || 7.52,
                margin_percentage: example.margin_percentage || 20.0,
                result: example.result || 22.53
              }
            }
          }
          
          // Load bulk import statistics
          if (data.bulk_import && data.bulk_import.statistics) {
            this.statistics = {
              totalProducts: data.bulk_import.statistics.total_products || 0,
              costsSet: data.bulk_import.statistics.costs_set || 0,
              missingCosts: data.bulk_import.statistics.missing_costs || 0,
              percentageSet: data.bulk_import.statistics.percentage_set || 0
            }
          }
        }
      }, (error) => {
        this.loading = false
        console.error('Error loading pricing & costs settings:', error)
        if (error.body && error.body.error) {
          this.errorMessage = error.body.error
        } else {
          this.errorMessage = 'Failed to load settings. Please try again.'
        }
      })
    },
    onMarginChange: function (value) {
      this.settings.defaultMarginPercentage = value
    },
    onToggleChange: function (checked) {
      this.settings.floorPriceAlertEnabled = checked
    },
    downloadCSVTemplate: function () {
      this.downloading = true
      this.errorMessage = ''
      
      // Use XMLHttpRequest for blob handling
      const xhr = new XMLHttpRequest()
      xhr.open('GET', '/api/v1/pricing_costs/csv_template', true)
      xhr.responseType = 'blob'
      
      xhr.onload = () => {
        this.downloading = false
        
        if (xhr.status === 200) {
          // Create blob URL and trigger download
          const blob = xhr.response
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          
          // Try to get filename from Content-Disposition header, fallback to default
          const contentDisposition = xhr.getResponseHeader('Content-Disposition')
          let filename = `product_costs_import_${new Date().toISOString().split('T')[0]}.csv`
          
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '')
            }
          }
          
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          
          this.successMessage = 'CSV template downloaded successfully!'
        } else {
          // Try to parse error message
          const blob = xhr.response
          const reader = new FileReader()
          reader.onload = () => {
            let errorMsg = 'Failed to download CSV template. Please try again.'
            try {
              const errorJson = JSON.parse(reader.result)
              errorMsg = errorJson.error || errorMsg
            } catch (e) {
              // Not JSON, use default message
            }
            this.errorMessage = errorMsg
          }
          reader.readAsText(blob)
        }
      }
      
      xhr.onerror = () => {
        this.downloading = false
        this.errorMessage = 'Failed to download CSV template. Please check your connection and try again.'
      }
      
      xhr.send()
    },
    handleFileSelected: function (file) {
      this.uploadedFile = file
      this.errorMessage = ''
    },
    handleFileRemoved: function () {
      this.uploadedFile = null
    },
    handleError: function (errorMessage) {
      this.errorMessage = errorMessage
    },
    uploadFile: function () {
      if (!this.uploadedFile) {
        this.errorMessage = 'Please select a file to upload'
        return
      }
      
      this.uploading = true
      this.errorMessage = ''
      this.successMessage = ''
      
      const formData = new FormData()
      formData.append('file', this.uploadedFile)
      
      this.$http.post('/api/v1/pricing_costs/bulk_import', formData).then((response) => {
        this.uploading = false
        
        if (response.body && response.body.success) {
          const stats = response.body.statistics || {}
          const message = `Import completed! Processed: ${stats.processed || 0}, Updated: ${stats.updated || 0}, Skipped: ${stats.skipped || 0}`
          
          if (response.body.import_errors && response.body.import_errors.length > 0) {
            const errorCount = response.body.import_errors.length
            this.errorMessage = `${errorCount} row(s) had errors. Please check the file and try again.`
          } else {
            this.successMessage = message
          }
          
          // Reload statistics
          this.loadSettings()
          
          // Clear uploaded file
          this.uploadedFile = null
          
          // After successful upload, save settings if needed
          if (this.saving) {
            this.saveSettingsAfterUpload()
          }
        } else {
          this.errorMessage = 'Import failed. Please try again.'
          this.saving = false
        }
      }, (error) => {
        this.uploading = false
        console.error('Error uploading file:', error)
        
        if (error.body && error.body.error) {
          this.errorMessage = error.body.error
        } else if (error.body && error.body.errors && Array.isArray(error.body.errors)) {
          this.errorMessage = error.body.errors.join(', ')
        } else {
          this.errorMessage = 'Failed to upload file. Please try again.'
        }
        this.saving = false
      })
    },
    saveSettingsAfterUpload: function () {
      const payload = {
        minimum_margin_percentage: this.settings.defaultMarginPercentage,
        floor_price_alert_enabled: this.settings.floorPriceAlertEnabled
      }
      
      this.$http.put('/api/v1/pricing_costs/settings', payload).then((response) => {
        this.saving = false
        
        if (response.body && response.body.success) {
          if (!this.successMessage) {
            this.successMessage = 'Settings saved successfully!'
          }
        } else {
          this.errorMessage = 'Failed to save settings. Please try again.'
        }
      }, (error) => {
        this.saving = false
        console.error('Error saving settings:', error)
        
        if (error.body && error.body.errors && Array.isArray(error.body.errors)) {
          this.errorMessage = error.body.errors.join(', ')
        } else if (error.body && error.body.error) {
          this.errorMessage = error.body.error
        } else {
          this.errorMessage = 'Failed to save settings. Please try again.'
        }
      })
    },
    saveSettings: function () {
      // Validate margin percentage
      if (this.settings.defaultMarginPercentage < 0 || this.settings.defaultMarginPercentage > 100) {
        this.errorMessage = 'Margin percentage must be between 0 and 100'
        return
      }
      
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      
      const payload = {
        minimum_margin_percentage: this.settings.defaultMarginPercentage,
        floor_price_alert_enabled: this.settings.floorPriceAlertEnabled
      }
      
      // If file is uploaded, upload it first, then save settings
      if (this.uploadedFile) {
        this.uploadFile()
        // Settings will be saved after successful upload
        return
      }
      
      this.$http.put('/api/v1/pricing_costs/settings', payload).then((response) => {
        this.saving = false
        
        if (response.body && response.body.success) {
          this.successMessage = 'Settings saved successfully!'
        } else {
          this.errorMessage = 'Failed to save settings. Please try again.'
        }
      }, (error) => {
        this.saving = false
        console.error('Error saving settings:', error)
        
        if (error.body && error.body.errors && Array.isArray(error.body.errors)) {
          this.errorMessage = error.body.errors.join(', ')
        } else if (error.body && error.body.error) {
          this.errorMessage = error.body.error
        } else {
          this.errorMessage = 'Failed to save settings. Please try again.'
        }
      })
    }
  }
}
</script>

<style>
@import './components/pricing-costs/styles/PricingCostsPage.css';
</style>



// WEBPACK FOOTER //
// PricingCostsPage.vue?25a6eade